import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PortalShell from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { FastDocs } from "@/components/portal/FastDocs";
import { fastDocsMissing, sendFile } from "@/lib/fastDocs";
import { Loader2, Check, Search, RefreshCw, Building2, FileText, Clock } from "lucide-react";

const money = (n) => `£${Math.round(Number(n) || 0).toLocaleString()}`;
const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return "—"; }
};

const helpCta = (
  <a href="mailto:ops@connex.is" className="rounded-full border border-white/15 hover:border-white/30 px-3.5 h-9 inline-flex items-center text-white transition-colors" data-testid="status-help">
    Need a hand?
  </a>
);

/** ApplyStatus — a customer checks where their application is, with no login. */
export default function ApplyStatus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ reference: "", email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.get(`/apply/status/${token}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setErr(e?.response?.data?.detail || "We couldn't find that application"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api.post("/apply/status", { reference: form.reference.trim(), email: form.email.trim() });
      setData(r.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "We couldn't find that application");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] grid place-items-center">
        <Loader2 className="size-6 animate-spin text-[#7da9ff]" />
      </div>
    );
  }

  if (data) return <StatusCard data={data} onReset={() => { setData(null); setErr(null); }} showReset={!token} />;

  return (
    <PortalShell variant="apply" kind="introducer" eyebrow="Connexis · Application tracking"
                 title="Where's my application?"
                 ctas={helpCta}
                 subtitle="Pop in your reference and the email you applied with — no password, no phone call.">
      <div className="mx-auto max-w-md" data-testid="apply-status-lookup">
        <form onSubmit={lookup} className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 space-y-4">
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Your reference</label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value.toUpperCase() })}
                   placeholder="CX-1A2B3C" required
                   className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white font-mono tracking-wider placeholder:text-slate-600 focus:border-[#0055FF]"
                   data-testid="status-reference" />
            <p className="mt-1.5 text-[11.5px] text-slate-500">It's in the email we sent when your application came in.</p>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Email you applied with</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   placeholder="you@yourcompany.co.uk" required
                   className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-600 focus:border-[#0055FF]"
                   data-testid="status-email" />
          </div>
          {err && (
            <div className="rounded-xl border border-amber-400/30 bg-amber-500/[0.08] px-3.5 py-3 text-[12.5px] text-amber-200" data-testid="status-error">
              {err}
            </div>
          )}
          <Button type="submit" disabled={busy}
                  className="w-full h-11 rounded-full bg-[#0055FF] hover:bg-[#0044CC] font-semibold"
                  data-testid="status-submit">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <><Search className="size-4 mr-2" /> Check my application</>}
          </Button>
          <p className="text-center text-[11.5px] text-slate-500">
            Rather ask a person? Email{" "}
            <a href="mailto:ops@connex.is" className="text-[#7da9ff] underline underline-offset-2">ops@connex.is</a>{" "}
            with your reference.
          </p>
        </form>
      </div>
    </PortalShell>
  );
}

function StatusCard({ data, onReset, showReset }) {
  const closed = data.closed;
  const [docs, setDocs] = useState(data.docs || []);
  const [count, setCount] = useState(data.documents || 0);
  const [ask] = useState(() => !!data.upload_key && fastDocsMissing(data.finance_type, data.docs));
  return (
    <PortalShell variant="apply" kind="introducer" eyebrow="Connexis · Application tracking"
                 title={data.title} ctas={helpCta}
                 subtitle={`Reference ${data.reference} · submitted ${fmt(data.submitted_at)}`}>
      <div className="mx-auto max-w-2xl space-y-5" data-testid="apply-status">
        {/* Progress */}
        <div className={`rounded-3xl border p-6 ${closed ? "border-white/10 bg-white/[0.03]" : "border-[#0055FF]/25 bg-[#0055FF]/[0.06]"}`}>
          <div className="flex items-start gap-4">
            {data.steps.map((s, i) => (
              <div key={s.key} className="flex-1 min-w-0" data-testid={`status-step-${s.key}`}>
                <div className="flex items-center">
                  <span className={`grid place-items-center size-7 rounded-full border text-[11px] font-bold shrink-0 transition-colors ${
                    s.state === "done" ? "bg-emerald-500 border-emerald-400 text-white"
                      : s.state === "current" ? "bg-[#0055FF] border-[#7da9ff] text-white animate-pulse"
                      : "bg-white/[0.04] border-white/15 text-slate-500"}`}>
                    {s.state === "done" ? <Check className="size-3.5" strokeWidth={3} /> : i + 1}
                  </span>
                  {i < data.steps.length - 1 && (
                    <span className={`h-px flex-1 ml-1 ${s.state === "done" ? "bg-emerald-500/60" : "bg-white/10"}`} />
                  )}
                </div>
                <div className={`mt-2 text-[11.5px] font-semibold leading-tight ${
                  s.state === "todo" ? "text-slate-500" : "text-white"}`}>{s.label}</div>
              </div>
            ))}
          </div>
          <p className="mt-5 text-[13.5px] text-slate-200 leading-relaxed" data-testid="status-message">{data.message}</p>
          {data.lender_count > 0 && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.05] px-3 h-7 text-[12px] text-slate-200"
                 data-testid="status-lender-count">
              <Building2 className="size-3.5 text-[#7da9ff]" />
              {data.lender_count} lender{data.lender_count === 1 ? "" : "s"} looking at it
            </div>
          )}
        </div>

        {ask && (
          <FastDocs financeType={data.finance_type} files={docs} testId="status-fast-docs"
                    foot="Optional — your application keeps moving either way. Anything you add goes straight to the team working on it."
                    onUpload={async (label, file, onProgress) => {
                      const r = await sendFile(`/apply/status/${data.upload_key}/upload`, file, { label }, onProgress);
                      setDocs((d) => [...d, r.file]);
                      setCount(r.documents);
                    }} />
        )}

        {/* What we hold */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Your application</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-3.5">
            <Row label="Business" value={data.business_name || "—"} />
            <Row label="Applicant" value={data.applicant_name || "—"} />
            <Row label="Looking for" value={`${money(data.amount_gbp)} · ${data.finance_type || "Business finance"}`} />
            <Row label="Introduced by" value={data.introducer || "—"} />
            <Row label="Submitted" value={fmt(data.submitted_at)} />
            <Row label="Last update" value={fmt(data.updated_at)} />
          </div>
          <div className="mt-4 flex items-center gap-3 flex-wrap text-[12px] text-slate-400">
            {count > 0 && (
              <span className="inline-flex items-center gap-1.5" data-testid="status-documents"><FileText className="size-3.5 text-[#7da9ff]" />
                {count} document{count === 1 ? "" : "s"} on file</span>
            )}
            <span className="inline-flex items-center gap-1.5"><Clock className="size-3.5 text-[#7da9ff]" />
              We update this page as things move</span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11.5px] text-slate-500 max-w-md">
            Questions? Email <a href="mailto:ops@connex.is" className="text-[#7da9ff] underline underline-offset-2">ops@connex.is</a>{" "}
            quoting {data.reference}, or speak to {data.introducer || "your introducer"}. This page isn't a credit
            agreement or an offer of finance.
          </p>
          <div className="flex items-center gap-2">
            {showReset && (
              <Button variant="outline" onClick={onReset}
                      className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/[0.06]"
                      data-testid="status-check-another">
                Check another
              </Button>
            )}
            <Button onClick={() => window.location.reload()}
                    className="rounded-full bg-white/[0.08] hover:bg-white/[0.14] text-white"
                    data-testid="status-refresh">
              <RefreshCw className="size-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
        </div>

        <div className="text-center">
          <Link to="/" className="text-[12px] text-slate-500 hover:text-slate-300 transition-colors">Connexis Group</Link>
        </div>
      </div>
    </PortalShell>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
      <div className="text-[13.5px] text-white mt-0.5 break-words">{value}</div>
    </div>
  );
}
